import { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Archive, Loader2, RotateCcw, Trash2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ftc-reports/archive`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const Row = ({ r, onRestore, onDelete, busy }) => (
  <li className="flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-800/60 p-2" data-testid={`archive-client-${r.id}`}>
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-bold text-white">{r.client_name || 'Unnamed client'}</p>
      <p className="text-[11px] text-zinc-400">{r.account_count ?? 0} accounts · archived {fmt(r.archived_at)} · auto-deletes {fmt(r.expires_at)}</p>
    </div>
    <button type="button" disabled={!!busy} onClick={() => onRestore(r)} data-testid={`archive-restore-${r.id}`}
      className="flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50">
      {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}Restore
    </button>
    <button type="button" disabled={!!busy} onClick={() => onDelete(r)} aria-label="Delete forever" data-testid={`archive-delete-${r.id}`}
      className="rounded-md border border-red-500/50 p-1.5 text-red-300 hover:bg-red-500/20 disabled:opacity-50"><Trash2 className="h-3.5 w-3.5" /></button>
  </li>
);

// "Archive Clients": saved FTC clients untouched 90+ days. Kept 1 year, restorable back to Previous FTC Reports.
export default function ArchiveClients({ onRestored }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const load = async () => {
    setOpen(true);
    setRows(null);
    try { setRows((await axios.get(API)).data); } catch { setRows([]); toast.error('Could not load the archive'); }
  };
  const restore = async (r) => {
    setBusy(r.id);
    try {
      await axios.post(`${API}/${r.id}/restore`);
      setRows((x) => x.filter((y) => y.id !== r.id));
      toast.success(`${r.client_name} is back in Previous FTC Reports`);
      onRestored?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Restore failed'); }
    setBusy('');
  };
  const remove = async (r) => {
    if (!window.confirm(`Permanently delete ${r.client_name}? This can't be undone.`)) return;
    setBusy(r.id);
    try { await axios.delete(`${API}/${r.id}`); setRows((x) => x.filter((y) => y.id !== r.id)); toast.success('Deleted for good'); } catch { toast.error('Delete failed'); }
    setBusy('');
  };
  return (
    <>
      <button type="button" onClick={load} data-testid="archive-clients-btn"
        className="flex items-center gap-1.5 rounded-md border border-amber-400/50 bg-amber-500/10 px-3 py-1.5 text-xs font-bold text-amber-200 hover:bg-amber-500/20">
        <Archive className="h-4 w-4" />Archive Clients
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/70 p-3 pt-16" data-testid="archive-clients-modal">
          <div className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-xl border border-zinc-700 bg-zinc-900 p-4">
            <div className="mb-1 flex items-center justify-between">
              <p className="flex items-center gap-1.5 font-bold text-white"><Archive className="h-4 w-4 text-amber-300" />Archive Clients</p>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" data-testid="archive-clients-close"><X className="h-4 w-4 text-zinc-400" /></button>
            </div>
            <p className="mb-3 text-[11px] text-zinc-400">Clients with no new FTC report for 90 days move here. They stay for 1 year unless you delete them. Restore sends them back to Previous FTC Reports with all their info.</p>
            {!rows ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>
              : rows.length === 0 ? <p className="py-6 text-center text-sm text-zinc-500" data-testid="archive-empty">No archived clients.</p>
                : <ul className="space-y-1.5">{rows.map((r) => <Row key={r.id} r={r} busy={busy} onRestore={restore} onDelete={remove} />)}</ul>}
          </div>
        </div>, document.body,
      )}
    </>
  );
}
